import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons/faArrowLeft';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { TypedMemo } from '../../../common/types';
import { useMusic24Capabilities } from '../../../music24/hooks/useMusic24Capabilities';
import { useUpgradePrompt } from '../../../music24/hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../../music24/config/music24PlanCapabilities';
import { useNextPlanForFeature } from '../../../music24/hooks/useNextPlanForFeature';
import { planNameMessages } from '../../../music24/config/music24PlanNames';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { useEditorContext } from '../SMLEditorContext';
import { editorMessages } from '../editorMessages';
import { describeBlock } from '../blocks/describeBlock';
import { PickerItem, pickerItems } from './pickerItems';
import { pickerMessages as m } from './pickerMessages';
import { useAddBlock } from './useAddBlock';
import { SMLPickerSongStep } from './SMLPickerSongStep';
import { SMLPickerBlocks } from './SMLPickerBlocks';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './SMLBlockPicker.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The block picker (design b1–b5, c6). `?add=1&after=<uuid>`; Back closes it. A card adds its block
 * at once and opens the row; Catalog links asks for the song first. A desktop modal, a full-height
 * sheet on a phone.
 */
const SMLBlockPickerComponent: React.FC = () => {
  const intl = useIntl();
  const { smartLink, url, isBioLink, artistName, isCompact } = useEditorContext();
  const { canUsePresave } = useMusic24Capabilities();
  const { nextPlan } = useNextPlanForFeature(SMLUpgradeFeature.presave);
  const openUpgrade = useUpgradePrompt();
  const { add, songs } = useAddBlock();
  const [step, setStep] = useState<'blocks' | 'song'>('blocks');
  const [isBusy, setBusy] = useState(false);
  const widgets = useMemo(() => smartLink.recentSmartLinkVersion.widgets ?? [], [smartLink]);
  const after = useMemo(() => widgets.find((widget) => widget.uuid === url.pickerAfter), [url.pickerAfter, widgets]);
  const items = useMemo(
    () =>
      pickerItems(intl, {
        isBioLink,
        canUsePresave,
        presavePlanName: nextPlan === null ? null : intl.formatMessage(planNameMessages[nextPlan.tier]),
        widgets,
        pageSongUuid: songs.pageSong?.uuid ?? null,
      }),
    [canUsePresave, intl, isBioLink, nextPlan, songs.pageSong, widgets]
  );

  const run = useCallback(
    (type: SmartLinkWidgetType, catalogItemUuid?: string) => {
      setBusy(true);
      add({ type, afterUUID: url.pickerAfter, catalogItemUuid })
        .catch(console.error)
        .finally(() => setBusy(false));
    },
    [add, url.pickerAfter]
  );
  const onPick = useCallback(
    (item: PickerItem) => {
      if (item.onPageUUID !== null) {
        url.openBlock(item.onPageUUID);
      } else if (item.locked) {
        url.closeLayer();
        openUpgrade(SMLUpgradeFeature.presave);
      } else if (item.type === SmartLinkWidgetType.presave) {
        setStep('song');
      } else {
        run(item.type);
      }
    },
    [openUpgrade, run, url]
  );
  const onSong = useCallback((song: CatalogItem) => run(SmartLinkWidgetType.presave, song.uuid), [run]);
  const backToBlocks = useCallback(() => setStep('blocks'), []);

  let sub = intl.formatMessage(isBioLink ? m.subEndBio : m.subEnd);
  if (after !== undefined) {
    sub = intl.formatMessage(m.subAfter, { name: describeBlock(intl, after, { artistName }).name });
  }

  return (
    <div className={clsx(styles.overlay, isCompact && styles.overlayCompact)} data-testid="block-picker">
      <button
        type="button"
        className={styles.scrim}
        onClick={url.closeLayer}
        aria-label={intl.formatMessage(editorMessages.close)}
      />
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label={intl.formatMessage(m.title)}
        aria-busy={isBusy}
      >
        <div className={styles.head}>
          {step === 'song' && (
            <button
              type="button"
              className={styles.iconButton}
              onClick={backToBlocks}
              aria-label={intl.formatMessage(m.back)}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
          )}
          <h2 className={styles.title}>{intl.formatMessage(step === 'song' ? m.songTitle : m.title)}</h2>
          <button
            type="button"
            className={styles.iconButton}
            onClick={url.closeLayer}
            aria-label={intl.formatMessage(editorMessages.close)}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <p className={styles.sub}>{step === 'song' ? intl.formatMessage(m.songSub) : sub}</p>
        {step === 'song' ? (
          <SMLPickerSongStep songs={songs} widgets={widgets} onPick={onSong} onOpenRow={url.openBlock} />
        ) : (
          <SMLPickerBlocks
            items={items}
            isBioLink={isBioLink}
            theme={smartLink.recentSmartLinkVersion.theme}
            onPick={onPick}
          />
        )}
      </div>
    </div>
  );
};

export const SMLBlockPicker = TypedMemo(SMLBlockPickerComponent);
